import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';

import InputMask from 'react-input-mask';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';

import SuccessModal from 'app/components/SuccessModal';
import CustomInput from 'app/components/CustomInput';

import { useAddAlias } from '../../api/useGetAliases';

import { dialogActions, dialogTitle, paperProps, title, secondaryText } from './add-alias-modal.sx';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  value: string;
  inputTitle?: string;
  type: string;
  businessKey?: string;
  handleMobileNumber: (value: string) => void;
  handleClose: () => void;
  openOtpModal?: () => void;
}

export const formatNumber = (phone: string) => {
  return phone?.replace(/\D/g, '');
};

const AddAliasModal = (props: Props) => {
  const { t } = useTranslation();
  const { open, handleClose, type, businessKey, openOtpModal, handleMobileNumber } = props;
  const [successModal, setSuccessModal] = useState(false);
  const [unSuccessModal, setUnSuccessModal] = useState(false);
  const [emailVal, setEmail] = useState('');
  const addAlias = useAddAlias();

  const methods = useForm({
    resolver: yupResolver(
      object({
        email: string().when([], {
          is: () => type === 'email',
          then: (schema) =>
            schema
              .required(t('anipay.schema_email_format_required'))
              .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('anipay.schema_email_format_wrong')),
          otherwise: (schema) => schema.notRequired(),
        }),
        phoneNumber: string().when([], {
          is: () => type === 'mobile',
          then: (schema) =>
            schema
              .required(t('anipay.schema_phoneNumber_required'))
              .test(
                'valid-phone',
                t('anipay.schema_phoneNumber_min_9_symbols'),
                (val) => (formatNumber(val)?.length ?? 0) >= 9
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    ),
    mode: 'onChange',
  });
  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    control,
    reset,
  } = methods;

  const handleSendAliasData = (data: { phoneNumber: string; email: string }) => {
    addAlias.mutate(
      {
        action: 'add_alias',
        type,
        ...(type === 'mobile' ? { phone: `994${formatNumber(data?.phoneNumber)}` } : { email: data?.email }),
        business_key: businessKey,
      },
      {
        onSuccess: () => {
          type === 'mobile' ? handleMobileNumber(`+994 ${data?.phoneNumber}`) : handleMobileNumber(`${data?.email}`);
          openOtpModal();
          handleClose();
        },
        onError: () => {
          handleClose();
        },
      }
    );
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: paperProps,
        }}
        onClose={handleClose}
      >
        <DialogTitle sx={{ ...dialogTitle, mb: '28px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Typography sx={title}>{t('anipay.addAlias')}</Typography>
            <Typography sx={secondaryText}>
              {type === 'mobile'
                ? t('anipay.verificationCodeSendMessage', { sendTo: '' })
                : t('anipay.verificationLinkSendMessage', { sendTo: '' })}
            </Typography>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit(handleSendAliasData)}>
          <DialogContent sx={{ pl: '44px' }}>
            {type === 'mobile' ? (
              <>
                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <InputMask mask="(99) 999 99 99" {...field} maskChar={null}>
                      <OutlinedInput
                        error={!!errors.phoneNumber?.message}
                        placeholder={'(00) 000 00 00'}
                        sx={{
                          width: '320px',
                          '& .MuiInputBase-input': {
                            p: 0,
                          },
                        }}
                        startAdornment={
                          <InputAdornment position="start">
                            <Typography sx={{ fontFamily: 'Yelo', fontWeight: 500 }}>+994</Typography>
                          </InputAdornment>
                        }
                      />
                    </InputMask>
                  )}
                />

                {errors.phoneNumber?.message && (
                  <Typography sx={{ color: 'red', fontSize: '14px', mt: 0.5 }}>
                    {errors.phoneNumber?.message}
                  </Typography>
                )}
              </>
            ) : (
              <CustomInput
                value={emailVal}
                handleChange={(e) => setEmail(e.target.value)}
                title={t('anipay.email')}
                placeholder={t('anipay.email')}
                rest={{ ...register('email') }}
                isValid={!!errors.email?.message}
                errorText={errors.email?.message}
              />
            )}
          </DialogContent>
          <DialogActions sx={dialogActions}>
            <Button sx={{ width: '100%' }} type={'submit'} disabled={addAlias?.isLoading || !isValid}>
              {t('commonActions.continue')}
            </Button>
            <Button
              color={'secondary'}
              variant={'outlined'}
              sx={{ ml: '0 !important', width: '100%' }}
              onClick={handleClose}
            >
              {t('anipay.back')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <SuccessModal open={successModal} close={setSuccessModal} successText={t('anipay.querySendSuccess')} />
      <SuccessModal
        unsuccess={true}
        open={unSuccessModal}
        close={setUnSuccessModal}
        successText={t('anipay.somethingWentWrong')}
      />
    </>
  );
};

export default AddAliasModal;
